// Client-side mirror of backend/routers/dashboard.py::compute_kpi_summary.
// Used to scope the Dashboard KPIs to an operator's own lines/orders (UI-only filter).
const DAY_MS = 86400000;
const round1 = (n) => Math.round(n * 10) / 10;
const parse = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isOnTime = (o) => {
  const due = parse(o.due_date);
  const done = parse(o.completed_at) || parse(o.estimated_completion) || parse(o.updated_at);
  if (!due || !done) return true;
  return done <= due;
};

function oeeEstimate(machines, total, delayed, completedCount) {
  const activeMachines = machines.filter((m) => m.status === "active").length;
  const availability = machines.length ? activeMachines / machines.length : 0.9;
  const performance = total ? 1 - delayed / total : 0.9;
  const quality = completedCount + delayed ? completedCount / (completedCount + delayed) : 0.97;
  return { oee: round1(availability * Math.max(performance, 0) * Math.max(quality, 0) * 100), availability, activeMachines };
}

function capacityAndLoad(orders, machines, shiftHours) {
  let totalLoad = 0;
  let totalCapacity = 0;
  const rows = machines.map((m) => {
    const load = orders.filter((o) => o.line_id === m.id && o.status !== "Completed").reduce((s, o) => s + Number(o.target_quantity || 0), 0);
    const capacity = Number(m.max_hourly_capacity || 0) * shiftHours;
    totalLoad += load;
    totalCapacity += capacity;
    return { name: m.line_code || m.name || "?", capacity, load };
  });
  return { rows, totalLoad, totalCapacity };
}

function otdTrend14d(completed) {
  const dated = completed.map((o) => [o, parse(o.completed_at)]).filter(([, ts]) => ts);
  const todayEnd = new Date();
  todayEnd.setUTCHours(23, 59, 59, 0);
  const trend = [];
  for (let i = 13; i >= 0; i--) {
    const dayEnd = new Date(todayEnd.getTime() - i * DAY_MS);
    const upto = dated.filter(([, ts]) => ts <= dayEnd).map(([o]) => o);
    const otd = upto.length ? round1((upto.filter(isOnTime).length / upto.length) * 100) : null;
    trend.push({ day: dayEnd.toLocaleDateString("en-US", { month: "short", day: "2-digit", timeZone: "UTC" }), otd, completed: upto.length });
  }
  return trend;
}

import { isOverdue } from "@/lib/batches";

export function computeKpis(orders, machines, shiftHours = 8) {
  const total = orders.length;
  const delayed = orders.filter((o) => o.status === "Delayed").length;
  // Urgent = High priority, still open, not yet Delayed/Overdue — the "catch before it slips" bucket.
  const urgent = orders.filter((o) => o.priority === "High" && !["Completed", "Cancelled", "Delayed"].includes(o.status) && !isOverdue(o)).length;
  const inProgress = orders.filter((o) => o.status === "In Progress").length;
  const pending = orders.filter((o) => o.status === "Pending").length;
  const completed = orders.filter((o) => o.status === "Completed");
  const otdRate = completed.length ? round1((completed.filter(isOnTime).length / completed.length) * 100) : 100;
  const { oee, availability, activeMachines } = oeeEstimate(machines, total, delayed, completed.length);
  const { rows, totalLoad, totalCapacity } = capacityAndLoad(orders, machines, shiftHours || 8);
  return {
    active_orders: total - completed.length,
    total_orders: total,
    delayed_orders: delayed,
    urgent_orders: urgent,
    in_progress_orders: inProgress,
    completed_orders: completed.length,
    pending_orders: pending,
    oee_estimate: oee,
    otd_rate: otdRate,
    availability: round1(availability * 100),
    total_machines: machines.length,
    active_machines: activeMachines,
    utilization: totalCapacity ? round1((totalLoad / totalCapacity) * 100) : 0,
    status_distribution: [
      { name: "Pending", value: pending },
      { name: "In Progress", value: inProgress },
      { name: "Delayed", value: delayed },
      { name: "Completed", value: completed.length },
    ],
    capacity_vs_load: rows,
    otd_trend: otdTrend14d(completed),
  };
}
